import { Card } from '@trafiflow/shared-ui'
import { Search, Grid, List, Sparkles, Play, Image as ImageIcon, Loader2, AlertCircle, RefreshCw, Bookmark, BookmarkCheck, Filter, ChevronDown, Target, Users } from 'lucide-react'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { fbAdsService, type FbAd } from '../../../../services/fb-ads'
import { AdDetailModal } from './AdDetailModal'
import { isVideoUrl } from './media'
import apiClient from '@/shared/lib/api'

const PAGE_SIZE = 24 // Ads per page for infinite scroll

// Competitor interface matching the brand context page
interface TrackedCompetitor {
  id: string
  name: string
  website?: string
  facebookPageId?: string
  category: string
  notes?: string
}

function AdCard({ ad, viewMode, onClick, onSave, isSaved }: { ad: FbAd; viewMode: 'grid' | 'list'; onClick: () => void; onSave: (e: React.MouseEvent) => void; isSaved: boolean }) {
  const [imageError, setImageError] = useState(false)
  const primaryMedia = ad.media_urls?.[0] || null
  const hasVideo = (ad.media_urls || []).some(isVideoUrl) || ad.media_type === 'video'
  const isVideo = isVideoUrl(primaryMedia)

  const renderMedia = () => {
    if (!primaryMedia || imageError) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          {hasVideo ? (
            <Play className="w-12 h-12 text-tf-text-muted" />
          ) : (
            <ImageIcon className="w-12 h-12 text-tf-text-muted" />
          )}
        </div>
      )
    }

    if (isVideo) {
      return (
        <div className="relative w-full h-full">
          <video
            src={primaryMedia}
            className="w-full h-full object-cover"
            muted
            preload="metadata"
            onError={() => setImageError(true)}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-black/30 flex items-center justify-center">
              <Play className="w-6 h-6 text-white fill-white" />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="relative w-full h-full">
        <img
          src={primaryMedia}
          alt={ad.page_name}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
        {hasVideo && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-black/30 flex items-center justify-center">
              <Play className="w-6 h-6 text-white fill-white" />
            </div>
          </div>
        )}
      </div>
    )
  }

  const platformBadges = ad.platforms?.length > 0 ? ad.platforms : ['facebook']

  return (
    <Card className={`overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${viewMode === 'list' ? 'flex' : ''}`} onClick={onClick}>
      <div className={`relative ${viewMode === 'list' ? 'w-48 h-32 flex-shrink-0' : 'aspect-video'}`}>
        {renderMedia()}
        {/* Save Button */}
        <button
          onClick={onSave}
          className={`absolute top-2 right-2 p-2 rounded-full transition-all ${ isSaved ? 'bg-blue-600 text-white' : 'bg-tf-bg-card/90 text-tf-text-muted hover:bg-tf-bg-card hover:text-blue-600' }`}
          title={isSaved ? 'Remove from saved' : 'Save ad'}
        >
          {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
        </button>
      </div>
      <div className="p-4 flex-1">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-600">
            {ad.page_name?.charAt(0).toUpperCase() || 'A'}
          </div>
          <span className="text-sm font-medium text-tf-text truncate">{ad.page_name || 'Unknown Brand'}</span>
        </div>
        {ad.ad_text && (
          <p className="text-sm text-tf-text-muted line-clamp-2 mb-3">
            {ad.ad_text}
          </p>
        )}
        <div className="flex items-center justify-between text-xs text-tf-text-muted">
          <div className="flex gap-1">
            {platformBadges.slice(0, 2).map((platform) => (
              <span key={platform} className="px-2 py-1 bg-tf-bg-hover rounded capitalize">
                {platform}
              </span>
            ))}
            {platformBadges.length > 2 && (
              <span className="px-2 py-1 bg-tf-bg-hover rounded">
                +{platformBadges.length - 2}
              </span>
            )}
          </div>
          <span className="capitalize">{ad.media_type}</span>
        </div>
      </div>
    </Card>
  )
}

export function Ads() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [ads, setAds] = useState<FbAd[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPlatform, setSelectedPlatform] = useState('All Platforms')
  const [selectedBrand, setSelectedBrand] = useState('All Brands')
  const [selectedMediaType, setSelectedMediaType] = useState('All Types')
  const [selectedAd, setSelectedAd] = useState<FbAd | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [savedAdIds, setSavedAdIds] = useState<Set<string>>(new Set())
  const [showFilters, setShowFilters] = useState(false)
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Tracked competitors state
  const [trackedCompetitors, setTrackedCompetitors] = useState<TrackedCompetitor[]>([])
  const [showOnlyTracked, setShowOnlyTracked] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('All Categories')

  // Fetch tracked competitors from company profile
  const fetchTrackedCompetitors = useCallback(async () => {
    try {
      const response = await apiClient.get('/my-company')
      const competitors = response.data?.profile?.overview?.competitors || []
      setTrackedCompetitors(competitors)
    } catch (err) {
      console.error('[Ads] Failed to fetch tracked competitors:', err)
    }
  }, [])

  useEffect(() => {
    fetchTrackedCompetitors()
  }, [fetchTrackedCompetitors])

  const handleAdClick = (ad: FbAd) => {
    setSelectedAd(ad)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedAd(null)
  }

  const handleSaveAd = (ad: FbAd, e: React.MouseEvent) => {
    e.stopPropagation()
    setSavedAdIds(prev => {
      const next = new Set(prev)
      if (next.has(ad.ad_id)) {
        next.delete(ad.ad_id)
        // TODO: Call API to remove from saved
      } else {
        next.add(ad.ad_id)
        // TODO: Call API to save ad
      }
      // Persist to localStorage for now
      localStorage.setItem('savedAdIds', JSON.stringify([...next]))
      return next
    })
  }

  // Load saved ads from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('savedAdIds')
    if (saved) {
      setSavedAdIds(new Set(JSON.parse(saved)))
    }
  }, [])

  const platforms = ['All Platforms', 'Facebook', 'Instagram', 'TikTok', 'YouTube']
  const mediaTypes = ['All Types', 'image', 'video', 'carousel']

  // Get unique categories from tracked competitors
  const competitorCategories = useMemo(() => {
    const categories = [...new Set(trackedCompetitors.map(c => c.category).filter(Boolean))]
    return ['All Categories', ...categories.sort()]
  }, [trackedCompetitors])

  // Get unique brands from ads
  const brands = useMemo(() => {
    const uniqueBrands = [...new Set(ads.map(ad => ad.page_name).filter(Boolean))]
    return ['All Brands', ...uniqueBrands.sort()]
  }, [ads])

  // Get tracked competitor names (case-insensitive matching)
  const trackedCompetitorNames = useMemo(() => {
    return trackedCompetitors.map(c => c.name.toLowerCase())
  }, [trackedCompetitors])

  // Get tracked competitor page IDs
  const trackedPageIds = useMemo(() => {
    return trackedCompetitors
      .filter(c => c.facebookPageId)
      .map(c => c.facebookPageId!)
  }, [trackedCompetitors])

  useEffect(() => {
    loadAds()
  }, [])

  const loadAds = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await fbAdsService.listAds()
      setAds(result)
      setDisplayCount(PAGE_SIZE) // Reset pagination on reload
    } catch (err) {
      console.error('[Ads] Error loading ads:', err)
      setError(err instanceof Error ? err.message : 'Failed to load ads')
    } finally {
      setLoading(false)
    }
  }

  const filteredAds = useMemo(() => {
    return ads.filter((ad) => {
      const matchesSearch = !searchTerm ||
        ad.page_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ad.ad_text?.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesPlatform = selectedPlatform === 'All Platforms' ||
        ad.platforms?.some(p => p.toLowerCase() === selectedPlatform.toLowerCase())

      const matchesBrand = selectedBrand === 'All Brands' ||
        ad.page_name === selectedBrand

      const matchesMediaType = selectedMediaType === 'All Types' ||
        ad.media_type?.toLowerCase() === selectedMediaType.toLowerCase()

      // Tracked competitors filter
      let matchesTracked = true
      if (showOnlyTracked && trackedCompetitors.length > 0) {
        const adBrandLower = ad.page_name?.toLowerCase() || ''
        const matchesByName = trackedCompetitorNames.some(name =>
          adBrandLower.includes(name) || name.includes(adBrandLower)
        )
        const matchesByPageId = trackedPageIds.includes(ad.page_id)
        matchesTracked = matchesByName || matchesByPageId
      }

      // Category filter (only applies when showOnlyTracked is true)
      let matchesCategory = true
      if (showOnlyTracked && selectedCategory !== 'All Categories') {
        const competitorsInCategory = trackedCompetitors
          .filter(c => c.category === selectedCategory)
          .map(c => c.name.toLowerCase())
        const adBrandLower = ad.page_name?.toLowerCase() || ''
        matchesCategory = competitorsInCategory.some(name =>
          adBrandLower.includes(name) || name.includes(adBrandLower)
        )
      }

      return matchesSearch && matchesPlatform && matchesBrand && matchesMediaType && matchesTracked && matchesCategory
    })
  }, [ads, searchTerm, selectedPlatform, selectedBrand, selectedMediaType, showOnlyTracked, trackedCompetitors, trackedCompetitorNames, trackedPageIds, selectedCategory])

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(PAGE_SIZE)
  }, [searchTerm, selectedPlatform, selectedBrand, selectedMediaType, showOnlyTracked, selectedCategory])

  // Visible ads (paginated)
  const visibleAds = useMemo(() => {
    return filteredAds.slice(0, displayCount)
  }, [filteredAds, displayCount])

  const hasMore = displayCount < filteredAds.length

  // Infinite scroll observer
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [target] = entries
    if (target.isIntersecting && hasMore && !loading) {
      setDisplayCount(prev => Math.min(prev + PAGE_SIZE, filteredAds.length))
    }
  }, [hasMore, loading, filteredAds.length])

  useEffect(() => {
    const element = loadMoreRef.current
    if (!element) return

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [handleObserver])

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-tf-text">Ads Research</h1>
        <p className="text-tf-text-muted mt-1">
          Discover and analyze top-performing ads from Facebook Ad Library
        </p>
      </div>

      {/* Tracked Competitors Quick Filter */}
      {trackedCompetitors.length > 0 && (
        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">
                {trackedCompetitors.length} tracked competitor{trackedCompetitors.length !== 1 ? 's' : ''}
              </span>
              <div className="flex gap-1 ml-2">
                {trackedCompetitors.slice(0, 3).map(c => (
                  <span key={c.id} className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                    {c.name}
                  </span>
                ))}
                {trackedCompetitors.length > 3 && (
                  <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                    +{trackedCompetitors.length - 3} more
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowOnlyTracked(!showOnlyTracked)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                showOnlyTracked
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-50'
              }`}
            >
              <Users className="w-4 h-4" />
              {showOnlyTracked ? 'Showing Tracked Only' : 'Show Tracked Only'}
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tf-text-muted" />
          <input
            type="text"
            placeholder="Search ads by brand or keyword..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${ showFilters ? 'bg-tf-feedback-info-bg border-blue-300 text-blue-600' : 'border-tf-border hover:bg-tf-bg-muted' }`}
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          <button
            onClick={loadAds}
            className="flex items-center gap-2 px-4 py-2.5 border border-tf-border rounded-lg hover:bg-tf-bg-muted transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <div className="flex border border-tf-border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 ${viewMode === 'grid' ? 'bg-tf-feedback-info-bg text-blue-600' : 'hover:bg-tf-bg-muted'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 ${viewMode === 'list' ? 'bg-tf-feedback-info-bg text-blue-600' : 'hover:bg-tf-bg-muted'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Filter Dropdowns */}
      {showFilters && (
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-tf-bg-muted rounded-lg">
          {/* Brand Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-tf-text-muted uppercase">Brand</label>
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="px-4 py-2 border border-tf-border rounded-lg bg-tf-bg-card focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]"
            >
              {brands.map((brand) => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>

          {/* Competitor Category Filter (only when tracked filter is on) */}
          {showOnlyTracked && trackedCompetitors.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-tf-text-muted uppercase">Competitor Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-purple-300 rounded-lg bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 min-w-[180px]"
              >
                {competitorCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          )}

          {/* Media Type Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-tf-text-muted uppercase">Media Type</label>
            <select
              value={selectedMediaType}
              onChange={(e) => setSelectedMediaType(e.target.value)}
              className="px-4 py-2 border border-tf-border rounded-lg bg-tf-bg-card focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px]"
            >
              {mediaTypes.map((type) => (
                <option key={type} value={type}>{type === 'All Types' ? type : type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* Platform Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-tf-text-muted uppercase">Platform</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="px-4 py-2 border border-tf-border rounded-lg bg-tf-bg-card focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px]"
            >
              {platforms.map((platform) => (
                <option key={platform} value={platform}>{platform}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          <div className="flex flex-col gap-1 justify-end">
            <button
              onClick={() => {
                setSelectedBrand('All Brands')
                setSelectedMediaType('All Types')
                setSelectedPlatform('All Platforms')
                setSelectedCategory('All Categories')
                setSearchTerm('')
                setShowOnlyTracked(false)
              }}
              className="px-4 py-2 text-sm text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-hover rounded-lg transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Active Filters Summary */}
      {(selectedBrand !== 'All Brands' || selectedMediaType !== 'All Types' || selectedPlatform !== 'All Platforms' || showOnlyTracked || selectedCategory !== 'All Categories') && (
        <div className="flex flex-wrap gap-2 mb-4">
          {showOnlyTracked && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              <Target className="w-3 h-3" />
              Tracked Competitors
              <button onClick={() => setShowOnlyTracked(false)} className="hover:text-purple-600">×</button>
            </span>
          )}
          {selectedCategory !== 'All Categories' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              Category: {selectedCategory}
              <button onClick={() => setSelectedCategory('All Categories')} className="hover:text-purple-600">×</button>
            </span>
          )}
          {selectedBrand !== 'All Brands' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Brand: {selectedBrand}
              <button onClick={() => setSelectedBrand('All Brands')} className="hover:text-blue-600">×</button>
            </span>
          )}
          {selectedMediaType !== 'All Types' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Type: {selectedMediaType}
              <button onClick={() => setSelectedMediaType('All Types')} className="hover:text-blue-600">×</button>
            </span>
          )}
          {selectedPlatform !== 'All Platforms' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Platform: {selectedPlatform}
              <button onClick={() => setSelectedPlatform('All Platforms')} className="hover:text-blue-600">×</button>
            </span>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-4" />
          <p className="text-tf-text-muted">Loading ads...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-tf-text font-medium mb-2">Failed to load ads</p>
          <p className="text-tf-text-muted text-sm mb-4">{error}</p>
          <button
            onClick={loadAds}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredAds.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <Sparkles className="w-12 h-12 text-tf-text-muted mb-4" />
          <p className="text-tf-text font-medium mb-2">No ads found</p>
          <p className="text-tf-text-muted text-sm text-center max-w-md">
            {showOnlyTracked && trackedCompetitors.length > 0
              ? 'No ads found from your tracked competitors. Make sure the competitor names match the ad library page names, or add their Facebook Page IDs in Brand Context settings.'
              : ads.length === 0
                ? 'Run the FB Ad Library ETL to collect ads for your brands.'
                : 'Try adjusting your search or filters.'}
          </p>
          {showOnlyTracked && (
            <button
              onClick={() => setShowOnlyTracked(false)}
              className="mt-4 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            >
              Show All Ads
            </button>
          )}
        </div>
      )}

      {/* Ads Grid */}
      {!loading && !error && visibleAds.length > 0 && (
        <>
          <p className="text-sm text-tf-text-muted mb-4">
            Showing {visibleAds.length} of {filteredAds.length} ads
            {showOnlyTracked && <span className="text-purple-600 ml-1">(from tracked competitors)</span>}
          </p>
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                : 'space-y-4'
            }
          >
            {visibleAds.map((ad) => (
              <AdCard
                key={ad.ad_id}
                ad={ad}
                viewMode={viewMode}
                onClick={() => handleAdClick(ad)}
                onSave={(e) => handleSaveAd(ad, e)}
                isSaved={savedAdIds.has(ad.ad_id)}
              />
            ))}
          </div>

          {/* Infinite scroll trigger */}
          <div ref={loadMoreRef} className="h-10 flex items-center justify-center mt-8">
            {hasMore && (
              <div className="flex items-center gap-2 text-tf-text-muted">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading more ads...</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Ad Detail Modal */}
      <AdDetailModal
        ad={selectedAd}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  )
}

export default Ads
